import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentHeaderFooterService } from '../document-header-footer.service';
import { DocumentHeader, HeaderFormData } from '../document-header-footer.interface';

@Component({
    selector: 'app-add-edit-header',
    templateUrl: './add-edit-header.component.html',
    styleUrls: ['./add-edit-header.component.scss']
})
export class AddEditHeaderComponent implements OnInit {
    form!: FormGroup;
    isLoading = false;
    mode: 'add' | 'edit' = 'add';
    header: DocumentHeader | null = null;
    previewImageUrl: string | null = null;

    contentTypes = [
        { value: 'text', label: 'Text' },
        { value: 'image', label: 'Imagine' },
        { value: 'both', label: 'Text + Imagine' }
    ];

    alignments = [
        { value: 'left', label: 'Stânga' },
        { value: 'center', label: 'Centru' },
        { value: 'right', label: 'Dreapta' }
    ];

    displayModes = [
        { value: 'first_page', label: 'Doar pe prima pagină' },
        { value: 'all_pages', label: 'Pe toate paginile' }
    ];

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<AddEditHeaderComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { mode: 'add' | 'edit'; header?: DocumentHeader },
        private service: DocumentHeaderFooterService,
        private snackBar: MatSnackBar
    ) {
        this.mode = data.mode;
        this.header = data.header || null;
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        this.form = this.fb.group({
            name: [this.header?.name || '', Validators.required],
            content_type: [this.header?.content_type || 'text', Validators.required],
            text_content: [this.header?.text_content || ''],
            image_path: [this.header?.image_path || ''],
            alignment: [this.header?.alignment || 'center', Validators.required],
            display_mode: [this.header?.display_mode || 'first_page', Validators.required],
            is_active: [this.header?.is_active !== false]
        });

        // Use image_url from backend for preview (full API URL)
        if (this.header?.image_url) {
            this.previewImageUrl = this.header.image_url;
        }
    }

    get showTextContent(): boolean {
        const type = this.form.get('content_type')?.value;
        return type === 'text' || type === 'both';
    }

    get showImageUpload(): boolean {
        const type = this.form.get('content_type')?.value;
        return type === 'image' || type === 'both';
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            // Validate file type
            if (!file.type.startsWith('image/')) {
                this.snackBar.open('Vă rugăm selectați o imagine', 'Închide', { duration: 3000 });
                return;
            }

            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                this.snackBar.open('Imaginea trebuie să fie mai mică de 2MB', 'Închide', { duration: 3000 });
                return;
            }

            this.isLoading = true;
            this.service.uploadHeaderImage(file).subscribe({
                next: (response: any) => {
                    this.form.patchValue({ image_path: response.data.path });
                    this.previewImageUrl = response.data.url;
                    this.isLoading = false;
                    this.snackBar.open('Imagine încărcată cu succes', 'Închide', { duration: 3000 });
                },
                error: (err) => {
                    console.error('Error uploading image:', err);
                    this.snackBar.open('Eroare la încărcarea imaginii', 'Închide', { duration: 3000 });
                    this.isLoading = false;
                }
            });
        }
    }

    removeImage(): void {
        this.form.patchValue({ image_path: '' });
        this.previewImageUrl = null;
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const payload: HeaderFormData = {
            ...this.form.value
        };

        if (this.mode === 'edit' && this.header) {
            payload.id = this.header.id;
        }

        this.isLoading = true;

        const request = this.mode === 'add'
            ? this.service.storeHeader(payload)
            : this.service.updateHeader(payload);

        request.subscribe({
            next: (response: any) => {
                this.snackBar.open(
                    this.mode === 'add' ? 'Antet creat cu succes' : 'Antet actualizat cu succes',
                    'Închide',
                    { duration: 3000 }
                );
                this.dialogRef.close(true);
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la salvarea antetului';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
                this.isLoading = false;
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
}
